<script setup>
import { useMediaQuery } from '@vueuse/core';
import { getConferenceTimezone, getPartnerDay } from '~~/shared/utilities/schedule';
import { canAccessPartnerDay } from '~~/shared/utilities/schedule/ticket';
import AgendaOverviewCard from '~/components/blocks/two-six/agenda/overview-card.vue';

const props = defineProps({
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    year: { type: Number, default: 2026 },
    day: { type: String, default: 'all' },
    randomize: { type: Boolean, default: false },
});

// viewDay: an empty day column hands off to All Sessions scoped to that day —
// agenda.vue owns the tab/view/day switch (UNB-623).
const emit = defineEmits(['viewDay']);

const ALL_CONFERENCE_DAYS = [
    { value: '2026-09-15', label: 'Tue • Sept 15' },
    { value: '2026-09-16', label: 'Wed • Sept 16' },
    { value: '2026-09-17', label: 'Thu • Sept 17' },
    { value: '2026-09-18', label: 'Fri • Sept 18' },
];

// Partner Day is invitation-only (UNB-600): its column exists only for a viewer
// whose Bizzabo registration reads partner_day 'Approved'. This list is the
// overview's OWN — agenda.vue's gated `dates` drives the List/Calendar day tabs
// and never reaches here — so the column had to be gated separately. The server
// already withholds the sessions, making this presentation: without it a
// non-approved viewer gets an empty "Tue • Sept 15" column advertising a day
// they can't see.
//
// Client-only, matching agenda.vue's day tab and useScheduleControls: this
// view's SSR HTML is CDN-cached and shared between viewers, so deciding on the
// server would freeze one viewer's access for everyone who got that cache entry.
// Ships without the column, revealed after mount — which also keeps hydration
// matching the cached markup.
const { user } = useUserSession();
const partnerDayReady = ref(false);
onMounted(() => { partnerDayReady.value = true; });
const showPartnerDay = computed(() => partnerDayReady.value && canAccessPartnerDay(user.value));

// Beyond Partner Day, the overview shows every conference day as a column — it's
// the all-days view, and its data fetch ignores the `day` filter. A `day` param
// can linger in the URL when toggling here from Calendar/List (which
// require/allow a single day); it must NOT collapse the overview to one column.
const conferenceDays = computed(() => {
    if (showPartnerDay.value) return ALL_CONFERENCE_DAYS;
    const partnerDay = getPartnerDay(props.year);
    return ALL_CONFERENCE_DAYS.filter(d => d.value !== partnerDay);
});

const sessionsByDay = computed(() => {
    const buckets = Object.fromEntries(conferenceDays.value.map(d => [d.value, []]));
    const sessions = props.data?.sessions || [];
    for (const session of sessions) {
        if (!session.start_date_and_time) continue;
        const dayKey = new Intl.DateTimeFormat('en-CA', { timeZone: getConferenceTimezone(props.year) })
            .format(new Date(session.start_date_and_time))
            .slice(0, 10);
        if (buckets[dayKey]) buckets[dayKey].push(session);
    }
    for (const day of Object.keys(buckets)) {
        buckets[day].sort((a, b) =>
            new Date(a.start_date_and_time) - new Date(b.start_date_and_time)
        );
    }
    return buckets;
});

// Swipe-view carousel — arrows page through the day columns wherever they don't
// all fit: one at a time on mobile, two at a time on tablet. Desktop shows all
// four, so no arrows.
//
// True wherever the day columns live in the horizontal swipe scroller — i.e.
// everywhere except desktop, where all four columns fit at once.
//
// Was mobile-only (max-767), which left tablet as the odd one out: it used the
// DESKTOP per-column headers inside the MOBILE swipe scroller, and those can
// never stick there. `overflow-x: auto` forces overflow-y to `auto` too (the
// spec won't let one axis scroll while the other stays visible), so the columns
// are a scroll container and a sticky child pins to THAT, not the page — and it
// has no vertical scroll to pin against. Mobile always dodged this by putting
// its day bar OUTSIDE the scroller; tablet now uses the same bar, so the header
// sticks at every width (Asana UNB-601).
const isSwipeView = useMediaQuery('(max-width: 1023px)', { ssrWidth: 768 });
const scrollContainer = ref(null);

// Default to the first non-partner-day (Wednesday) so mobile opens on the
// main conference day rather than the partner-only Tuesday.
const defaultDayIdx = computed(() => {
    const partnerDay = getPartnerDay(props.year);
    const idx = conferenceDays.value.findIndex(d => d.value !== partnerDay);
    return Math.max(0, idx);
});
const currentDayIndex = ref(0);

// The scroll step is ONE column, which is not the same as the container: mobile
// fits one column per screen, tablet fits two. Measuring the column (plus the
// row's gap) keeps the paging honest at both. Using clientWidth — as this did
// when it was mobile-only, where the two happened to be equal — makes tablet
// page two days per press and report half-indices back from scroll.
const columnStep = () => {
    const el = scrollContainer.value;
    if (!el) return 0;
    const first = el.firstElementChild;
    if (!first) return el.clientWidth;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
};

// How many columns are on screen — 1 on mobile, 2 on tablet. The last day can
// never scroll to the leftmost position when two are visible, so this is also
// what stops the Next arrow offering a page that can't happen.
const visibleColumns = () => {
    const el = scrollContainer.value;
    const step = columnStep();
    if (!el || !step) return 1;
    return Math.max(1, Math.round(el.clientWidth / step));
};

const maxDayIndex = ref(0);
const recomputeMaxDayIndex = () => {
    maxDayIndex.value = Math.max(0, conferenceDays.value.length - visibleColumns());
};

const onScroll = () => {
    const el = scrollContainer.value;
    if (!el) return;
    const step = columnStep();
    if (!step) return;
    // The snap track stops at column boundaries — round scrollLeft to find the
    // currently-snapped column.
    const newIdx = Math.round(el.scrollLeft / step);
    currentDayIndex.value = Math.max(0, Math.min(conferenceDays.value.length - 1, newIdx));
};

const scrollToDay = (idx) => {
    const el = scrollContainer.value;
    if (!el) return;
    el.scrollTo({ left: columnStep() * idx, behavior: 'smooth' });
};

const handleNext = () => scrollToDay(currentDayIndex.value + 1);
const handlePrev = () => scrollToDay(currentDayIndex.value - 1);

const canPrev = computed(() => currentDayIndex.value > 0);
const canNext = computed(() => currentDayIndex.value < maxDayIndex.value);

// Sticky day headers (desktop) follow the 2026 site nav so they tuck below it
// when it's showing and sit at the very top when it auto-hides on scroll-down.
const stickyTop = ref(0);
const updateStickyTop = () => {
    const nav = document.querySelector('nav.block-2026-header');
    stickyTop.value = nav?.classList.contains('header-visible') ? nav.offsetHeight : 0;
};

// The 1px rule under the day header shows only while the header is actually
// PINNED, not merely sticky-positioned (Asana UNB-601).
//
// CSS exposes no "currently stuck" state, so measure it: a pinned sticky element
// sits exactly at the offset it sticks to. That offset is a moving target — the
// site nav auto-hides on scroll, so stickyTop flips between the nav's height and
// 0 — which is why this compares against stickyTop rather than a fixed number,
// and why an IntersectionObserver with a baked-in rootMargin wouldn't hold.
const dayHeaderEls = ref([]);
const isStuck = ref(false);
let stuckRaf = false;
const measureStuck = () => {
    if (stuckRaf) return;
    stuckRaf = true;
    requestAnimationFrame(() => {
        stuckRaf = false;
        const el = dayHeaderEls.value[0];
        // Every column's header pins at the same moment, so the first speaks for
        // all of them. Below desktop it isn't sticky at all (it's display:none —
        // the swipe bar takes over), so there's no pinned state and no rule.
        if (!el || getComputedStyle(el).position !== 'sticky') {
            isStuck.value = false;
            return;
        }
        isStuck.value = Math.round(el.getBoundingClientRect().top) <= stickyTop.value + 1;
    });
};

const onWindowScroll = () => { updateStickyTop(); measureStuck(); };
const onResize = () => { recomputeMaxDayIndex(); measureStuck(); };

onMounted(() => {
    scrollContainer.value?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    updateStickyTop();
    recomputeMaxDayIndex();
    measureStuck();
    // Scroll to Wednesday on mobile so the partner-only Tuesday is off-screen
    nextTick(() => {
        const idx = defaultDayIdx.value;
        if (idx > 0) {
            currentDayIndex.value = idx;
            scrollToDay(idx);
        }
    });
});
onBeforeUnmount(() => {
    scrollContainer.value?.removeEventListener('scroll', onScroll);
    window.removeEventListener('scroll', onWindowScroll);
    window.removeEventListener('resize', onResize);
});

// The column count changes with the day list (Partner Day comes and goes for an
// approved viewer after mount), which moves the last reachable index.
watch(conferenceDays, () => { recomputeMaxDayIndex(); measureStuck(); });
</script>

<template>
    <div :style="{ '--ov-sticky-top': stickyTop + 'px' }">
        <!-- Swipe-view header (mobile + tablet): arrows pinned left; current day
             centered above the card column with the next day flowing to its right
             and dissolving into the page background via a left→right
             transparent-to-pink gradient (matching Figma). Sits OUTSIDE the scroll
             container so it stays fixed while the columns swipe beneath — which is
             the whole reason it can be sticky at all, and why tablet now uses it
             too (UNB-601). -->
        <div v-if="isSwipeView" data-test="ov-swipe-bar" class="relative flex items-center h-[42px] py-[12px] box-content w-full mb-[16px] overflow-hidden sticky z-[10] bg-(--pink) [top:var(--ov-sticky-top,0px)]">
            <!-- Arrows: absolutely pinned left and vertically centered (matching the
                 42px row height) so the day labels can center across the full row. -->
            <div v-if="conferenceDays.length > 1" class="absolute left-0 inset-y-0 flex items-center z-[1]">
                <FragmentsCarouselControls
                    :has-prev="canPrev"
                    :has-next="canNext"
                    variant="magenta"
                    prev-label="Previous day"
                    next-label="Next day"
                    @prev="handlePrev"
                    @next="handleNext" />
            </div>
            <!-- Current day: centered across the full row (12px left padding cheats
                 the optical centering against the left-pinned arrows). -->
            <div class="w-full flex items-center justify-center">
                <span class="pl-[12px] font-hubspot-sans font-medium text-[19px] leading-none tracking-[1.52px] uppercase text-(--magenta)">
                    {{ conferenceDays[currentDayIndex]?.label }}
                </span>
            </div>
            <!-- Next day: absolutely pinned to the right edge, dissolving under the
                 gradient that's laid over it. Tappable to advance to that day
                 (the gradient above is pointer-events-none so taps fall through);
                 keyboard/SR users use the carousel arrows. -->
            <span
                v-if="conferenceDays[currentDayIndex + 1]"
                aria-hidden="true"
                role="button"
                @click="handleNext"
                class="absolute right-[12px] inset-y-0 flex items-center font-hubspot-sans font-medium text-[19px] leading-none tracking-[1.52px] uppercase text-(--magenta) whitespace-nowrap cursor-pointer">
                {{ conferenceDays[currentDayIndex + 1]?.label.split('•')[0].trim() }}&nbsp; •&nbsp;
            </span>
            <div
                v-if="conferenceDays[currentDayIndex + 1]"
                aria-hidden="true"
                class="pointer-events-none absolute inset-y-0 right-0 w-[14%] bg-[linear-gradient(to_right,transparent,var(--pink)_80%)]"></div>
        </div>

        <div
            ref="scrollContainer"
            class="flex gap-[20px] lg:gap-0 items-start w-full overflow-x-auto snap-x snap-mandatory scrollbar-hide lg:overflow-visible lg:snap-none lg:items-stretch">
            <div
                v-for="(day, dayIndex) in conferenceDays"
                :key="day.value"
                class="snap-start shrink-0 w-full md:w-[calc(50%-10px)] lg:w-auto lg:flex-1 lg:min-w-0 lg:snap-none flex flex-col gap-[20px]"
            >
                <!-- Day column header — DESKTOP ONLY (below lg the swipe bar above
                     carries the day label, arrows and all). It's desktop-only because
                     that's the only width where it can stick: below lg these columns
                     sit in a horizontal scroll container, and a sticky child pins to
                     that rather than the page.
                     20px top/bottom + a 1px bottom rule, per the comp (3619:37446).
                     The rule is --dark-pink, per that comp — UNB-601's text asks for
                     magenta, but it says "per designs" and the design says dark-pink;
                     Jack's call was to follow the Figma.

                     The rule shows only while the header is PINNED (isStuck), per
                     Nick on UNB-601 — it marks the header floating over the cards,
                     so it has no business being there when the header is just sitting
                     in the page. The border is always present but transparent, and
                     only its COLOUR swaps: adding/removing border-b would shift the
                     layout by a pixel every time you crossed the pin point. The two
                     colours are swapped rather than layered, since competing
                     border-colour utilities resolve by stylesheet order, not class
                     order.

                     The comp draws that rule as ONE line under the whole header row.
                     These headers live inside each day column and are w-full, and at lg
                     the columns touch (the row's gap moved to lg:gap-0, with the 20px
                     inter-column spacing now carried as a 10px card inset per column).
                     Because adjacent full-width headers meet edge-to-edge, their bottom
                     borders already form one continuous line — no per-column connector
                     needed. (That inset also fixes the hover-glow bleed: the card glow
                     used to spill into the old flex gap and show past the header's
                     left/right edges; the full-width header now covers that strip.) -->
                <div
                    :ref="el => { if (el) dayHeaderEls[dayIndex] = el }"
                    data-test="ov-day-header"
                    class="hidden lg:flex relative items-center justify-center gap-[10px] pb-[20px] w-full border-b lg:sticky lg:z-[10] lg:bg-(--pink) lg:pt-[20px] lg:[top:var(--ov-sticky-top,0px)]"
                    :class="isStuck ? 'border-(--dark-pink)' : 'border-transparent'"
                >
                    <span class="font-hubspot-sans font-medium text-[19px] lg:text-[20px] leading-none tracking-[1.52px] lg:tracking-[1.6px] uppercase text-(--magenta) text-center">
                        {{ day.label }}
                    </span>
                </div>

                <!-- Session cards (removals apply immediately). Inset 10px at desktop
                     (UNB): the column gap was retired to lg:gap-0 so the full-width
                     sticky header spans the space where a card's hover-glow used to
                     bleed sideways into the inter-column gap and show past the header's
                     left/right edges. The 10px inset keeps the same 20px between
                     adjacent columns' cards while the header now covers that strip. -->
                <div class="relative flex flex-col gap-[20px] lg:px-[10px]">
                    <AgendaOverviewCard
                        v-for="session in sessionsByDay[day.value]"
                        :key="session.id || session.unique_id"
                        :session="session"
                        :year="year"
                        :is-full="!!session.is_full"
                        :action-required="!!session.user_added && session.session_reservations === true && !session.user_has_seat && !session.user_waitlisted"
                        :has-saved-seat="!!session.user_has_seat"
                    />
                </div>

                <!-- Empty state. The link answers "where do I go to add sessions?"
                     (UNB-623): you can't add from My Schedule, so an empty day column
                     was a dead end. It hands off to All Sessions → Details scoped to
                     this day. -->
                <div
                    v-if="!loading && sessionsByDay[day.value].length === 0"
                    class="flex flex-col items-center gap-[8px] w-full py-[12px]"
                >
                    <p class="font-hubspot-sans text-[14px] text-(--dark-pink) text-center m-0">No sessions scheduled.</p>
                    <button
                        type="button"
                        data-test="ov-empty-see-sessions"
                        class="font-hubspot-sans text-[14px] text-(--magenta) underline decoration-dotted underline-offset-[3px] cursor-pointer transition-colors duration-200 hover:text-(--purple) hover:no-underline"
                        @click="emit('viewDay', day.value)"
                    >See Sessions for this day</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>
